import React, {useEffect, useRef, useState, useCallback} from 'react';
import {Button, Switch, Select, Icon, Loader} from '@gravity-ui/uikit';
import {TrashBin, Plus} from '@gravity-ui/icons';
import {ActionModal, FormInput, ParagraphsEditor, ErrorModal, useErrorHandler} from '../../../../shared';
import {ImageUploadButton, PreparedImageData, fundraiserImageApi} from '../../../fundraiserImages';
import {useEditStoryScreenForm} from '../../model';
import {StoryParagraphModel, StoryScreenModel, StoryMediaType} from '../../../../entities';
import styles from './EditStoryScreenModal.module.scss';

interface EditStoryScreenModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  storyId: string;
  screen: StoryScreenModel;
}

const themeOptions = [
  {value: 'Light', content: 'Светлая'},
  {value: 'Dark', content: 'Темная'},
];

const buttonThemeOptions = [
  {value: 'Default', content: 'По умолчанию'},
  {value: 'White', content: 'Белая'},
  {value: 'Black', content: 'Черная'},
];

const mediaTypeOptions = [
  {value: 'image', content: 'Изображение'},
  {value: 'video', content: 'Видео'},
];

export const EditStoryScreenModal: React.FC<EditStoryScreenModalProps> = ({open, setOpen, storyId, screen}) => {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    isLoading,
    submitError,
    resetForm,
    submitForm,
    setFieldValue,
    isValid,
  } = useEditStoryScreenForm({storyId, screenId: screen.id!, setOpen, screen});

  const {isErrorModalOpen, errorInfo, handleError, handleClose} = useErrorHandler({
    resourceName: 'Экран',
    title: 'Ошибка при редактировании экрана',
  });

  const [isVideoUploading, setIsVideoUploading] = useState(false);
  const videoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (submitError) {
      handleError(submitError);
    }
  }, [submitError, handleError]);

  const handleParagraphsChange = (paragraphs: StoryParagraphModel[]) => {
    setFieldValue('paragraphs', paragraphs, true);
  };

  const handleImagePrepared = (data: PreparedImageData, previewUrl: string) => {
    setFieldValue('imageData', data);
    setFieldValue('imagePreview', previewUrl);
  };

  const handleRemoveImage = () => {
    setFieldValue('imageData', null);
    setFieldValue('imagePreview', null);
  };

  const handleVideoSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;
    const file = event.target.files[0];

    if (!file.type.startsWith('video/')) {
      return;
    }

    setIsVideoUploading(true);
    try {
      const response = await fundraiserImageApi.prepareUpload([file.type]);
      if (!response.items || response.items.length === 0 || !response.items[0].url) {
        throw new Error('Ошибка подготовки загрузки видео');
      }

      const uploadItem = response.items[0];

      const buffer = await file.arrayBuffer();
      const uploadResponse = await fetch(uploadItem.url, {
        method: 'PUT',
        headers: {'Content-Type': file.type},
        body: buffer,
      });

      if (!uploadResponse.ok) {
        throw new Error('Ошибка загрузки видео');
      }

      const previewUrl = URL.createObjectURL(file);

      setFieldValue('videoUploadId', uploadItem.id);
      setFieldValue('videoPreview', previewUrl);
    } catch (error) {
      console.error('Ошибка загрузки видео:', error);
    } finally {
      setIsVideoUploading(false);
      event.target.value = '';
    }
  }, [setFieldValue]);

  const handleRemoveVideo = () => {
    setFieldValue('videoUploadId', null);
    setFieldValue('videoPreview', null);
  };

  const handleSwitchChange = (field: string) => (checked: boolean) => {
    setFieldValue(field, checked, true);
  };

  const handleSelectChange = (field: string) => (values: string[]) => {
    setFieldValue(field, values[0], true);
  };

  const handleMediaTypeChange = (values: string[]) => {
    const newType = values[0] as StoryMediaType;
    setFieldValue('mediaType', newType, true);
    if (newType === 'image') {
      setFieldValue('videoUploadId', null);
      setFieldValue('videoPreview', null);
    } else {
      setFieldValue('imageData', null);
      setFieldValue('imagePreview', null);
    }
  };

  return (
    <>
      <ActionModal
        open={open}
        onClose={() => setOpen(false)}
        setOpen={setOpen}
        resetForm={resetForm}
        submitForm={submitForm}
        isLoading={isLoading}
        size="m"
        buttonsSize="l"
        caption="Редактировать экран"
        textButtonApply="Сохранить"
        textButtonCancel="Отменить"
        viewActionButton={isValid ? 'action' : 'outlined'}>
        <div className={styles.form}>
          <div className={styles.paragraphsField}>
            <ParagraphsEditor
              paragraphs={values.paragraphs}
              onChange={handleParagraphsChange}
              label="Текст истории"
              placeholder="Текст"
            />
            {touched.paragraphs && errors.paragraphs && typeof errors.paragraphs === 'string' && (
              <div className={styles.errorText}>{errors.paragraphs}</div>
            )}
          </div>

          <div className={styles.switchField}>
            <label className={styles.switchLabel}>Расположение текста снизу</label>
            <Switch size="l" checked={values.alignToBottom} onUpdate={handleSwitchChange('alignToBottom')} />
          </div>

          <div className={styles.switchField}>
            <label className={styles.switchLabel}>Показывать кнопку</label>
            <Switch size="l" checked={values.showButton} onUpdate={handleSwitchChange('showButton')} />
          </div>

          {values.showButton && (
            <>
              <FormInput
                label="Текст кнопки"
                name="buttonText"
                placeholder="Текст кнопки"
                value={values.buttonText}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.buttonText && errors.buttonText}
              />

              <FormInput
                label="URL кнопки"
                name="buttonUrl"
                placeholder="URL кнопки"
                value={values.buttonUrl}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.buttonUrl && errors.buttonUrl}
              />

              <div className={styles.selectField}>
                <label className={styles.selectLabel}>Тема кнопки</label>
                <Select
                  size="xl"
                  width="max"
                  value={[values.buttonTheme]}
                  onUpdate={handleSelectChange('buttonTheme')}
                  options={buttonThemeOptions}
                />
              </div>
            </>
          )}

          <div className={styles.selectField}>
            <label className={styles.selectLabel}>Тема экрана</label>
            <Select
              size="xl"
              width="max"
              value={[values.theme]}
              onUpdate={handleSelectChange('theme')}
              options={themeOptions}
            />
          </div>

          <div className={styles.selectField}>
            <label className={styles.selectLabel}>Медиа</label>
            <Select
              size="xl"
              width="max"
              value={[values.mediaType]}
              onUpdate={handleMediaTypeChange}
              options={mediaTypeOptions}
            />
          </div>

          {values.mediaType === 'image' && (
            <div className={styles.imageField}>
              <label className={styles.imageLabel}>Изображение</label>
              <div className={styles.imageUpload}>
                {!values.imagePreview && (
                  <ImageUploadButton isCreating={true} aspectRatio={240 / 427} onPrepareImageData={handleImagePrepared} />
                )}
                {values.imagePreview && (
                  <div className={styles.imagePreview}>
                    <img src={values.imagePreview} alt="Превью" />
                    <div className={styles.imageActions}>
                      <Button
                        view="flat"
                        size="s"
                        onClick={handleRemoveImage}
                        className={styles.removeImageBtn}
                        title="Удалить изображение">
                        <Icon data={TrashBin} size={14} />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {values.mediaType === 'video' && (
            <div className={styles.imageField}>
              <label className={styles.imageLabel}>Видео</label>
              <div className={styles.imageUpload}>
                {!values.videoPreview && !isVideoUploading && (
                  <>
                    <Button view="normal" size="xl" onClick={() => videoInputRef.current?.click()}>
                      <Icon data={Plus} size={20} />
                    </Button>
                    <input
                      ref={videoInputRef}
                      type="file"
                      accept="video/mp4,video/webm"
                      style={{display: 'none'}}
                      onChange={handleVideoSelect}
                    />
                  </>
                )}
                {isVideoUploading && (
                  <div className={styles.videoLoading}>
                    <Loader size="s" />
                    <span>Загрузка видео...</span>
                  </div>
                )}
                {values.videoPreview && !isVideoUploading && (
                  <div className={styles.videoPreview}>
                    <video src={values.videoPreview} className={styles.videoElement} muted />
                    <div className={styles.imageActions}>
                      <Button
                        view="flat"
                        size="s"
                        onClick={handleRemoveVideo}
                        className={styles.removeImageBtn}
                        title="Удалить видео">
                        <Icon data={TrashBin} size={14} />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </ActionModal>

      <ErrorModal open={isErrorModalOpen} onClose={handleClose} title={errorInfo.title} message={errorInfo.message} />
    </>
  );
};
